import { memo, type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

const tableShell = tv({
  slots: {
    root: [
      'rp-table-container w-full rounded-lg border border-[var(--color-an-border)] bg-[var(--color-an-surface)]',
      'max-lg:overflow-x-auto',
      'max-lg:[&_table]:min-w-max',
      'max-lg:[&_th:first-child]:sticky max-lg:[&_td:first-child]:sticky',
      'max-lg:[&_th:first-child]:left-0 max-lg:[&_td:first-child]:left-0',
      'max-lg:[&_th:first-child]:z-[calc(var(--z-sticky)+1)] max-lg:[&_td:first-child]:z-[var(--z-sticky)]',
      'max-lg:[&_th:first-child]:bg-[var(--color-an-surface,white)]',
      'max-lg:[&_td:first-child]:bg-[var(--color-an-surface,white)]',
      'max-lg:[&_th:first-child]:shadow-[var(--elevation-1)]',
      'max-lg:[&_td:first-child]:shadow-[var(--elevation-1)]',
    ].join(' '),
    table: 'w-full border-collapse text-sm text-[var(--color-an-text)]',
    head: 'border-b border-[var(--color-an-border)] bg-[var(--color-an-bg-elevated)]',
    body: '',
    foot: 'border-t border-[var(--color-an-border)] bg-[var(--color-an-surface-muted)]',
  },
  variants: {
    striped: {
      true: { body: '[&_tr:nth-child(even)]:bg-[var(--color-an-surface-muted)]' },
      false: { body: '' },
    },
  },
  defaultVariants: {
    striped: false,
  },
});

type TableShellVariants = VariantProps<typeof tableShell>;

export interface TableShellProps extends TableShellVariants {
  readonly children: ReactNode;
  readonly className?: string;
  readonly caption?: string;
  readonly 'aria-label'?: string;
}

/**
 * Semantic table wrapper for admin lists migrating off global App.css table rules (PR-2B).
 * L33 EXCEPTION twin: same `max-lg:overflow-x-auto` + sticky first-col parity as DataTableShell
 * (no tabindex — avoids axe scrollable-region-focusable + jsx-a11y conflicts).
 * Density is fixed (`text-sm`) — no compact|comfortable API (G10 ban).
 */
export const TableShell = memo<TableShellProps>(
  ({ children, className, caption, striped, 'aria-label': ariaLabel }) => {
    const slots = tableShell({ striped });
    return (
      <div className={slots.root({ className })}>
        <table className={slots.table()} aria-label={ariaLabel}>
          {caption !== undefined && caption.length > 0 ? <caption className="sr-only">{caption}</caption> : null}
          {children}
        </table>
      </div>
    );
  },
);
TableShell.displayName = 'TableShell';

export interface TableShellSectionProps {
  readonly children: ReactNode;
  readonly className?: string;
}

export const TableShellHead = memo<TableShellSectionProps>(({ children, className }) => {
  const slots = tableShell();
  return <thead className={slots.head({ className })}>{children}</thead>;
});
TableShellHead.displayName = 'TableShellHead';

export const TableShellBody = memo<TableShellSectionProps>(({ children, className }) => {
  const slots = tableShell();
  return <tbody className={slots.body({ className })}>{children}</tbody>;
});
TableShellBody.displayName = 'TableShellBody';

export const TableShellFoot = memo<TableShellSectionProps>(({ children, className }) => {
  const slots = tableShell();
  return <tfoot className={slots.foot({ className })}>{children}</tfoot>;
});
TableShellFoot.displayName = 'TableShellFoot';
